<script setup lang="ts">
import type { AppButtonState } from '@libs/ui';
import { AppButton, AppFlicker, AppRawButton, AppSvgIcon } from '@libs/ui';

import { useGlobalCurrencyStore } from '../../core/stores/globalCurrencyStore';
import { useItineraryStore, usePaxStore } from '../../core/stores';
import { useTripSummaryStore } from '../../pages/tripOverview/stores';

const globalCurrencyStore = useGlobalCurrencyStore();
const paxStore = usePaxStore();
const tripSummaryStore = useTripSummaryStore();
const itineraryStore = useItineraryStore();

interface PriceSummaryFooterWithSaveTripMobileProps {
  buttonState?: AppButtonState;
  buttonText: string;
}
const props = withDefaults(
  defineProps<PriceSummaryFooterWithSaveTripMobileProps>(),
  {
    buttonState: 'enabled',
  }
);

interface PriceSummaryFooterWithSaveTripMobileEmits {
  (eventName: 'button-clicked'): void;
}

const emits = defineEmits<PriceSummaryFooterWithSaveTripMobileEmits>();
</script>

<template>
  <div class="price-summary-footer-with-save-trip-mobile">
    <div class="price-summary-footer-with-save-trip-mobile-wrapper">
      <AppRawButton
        class="save-trip-button"
        @click="tripSummaryStore.openShareWidget"
      >
        <div class="save-trip-button-content">
          <AppSvgIcon name="save" size="18" class="save-trip-button-icon" />
          <span class="save-trip-button-text">
            {{ $t('planner.share_or_save_trip') }}
          </span>
        </div>
      </AppRawButton>

      <div class="price-and-button-wrapper">
        <div class="total-price">
          <AppFlicker
            v-if="itineraryStore.isSearchingItineraries"
            width="spacing-40"
            height="font-size-h5"
          />
          <div
            v-else
            class="text"
            :class="{ gap: !tripSummaryStore.tripGrossPrice }"
          >
            {{ $t('planner.price_for') }}
            {{ paxStore.numberOfTravelers }}
            {{
              $t(
                'planner.pax.travelers',
                paxStore.numberOfTravelers
              ).toLocaleLowerCase()
            }}
          </div>
          <div class="price">
            <div v-if="tripSummaryStore.tripGrossPrice" class="old-amount">
              <AppFlicker
                v-if="itineraryStore.isSearchingItineraries"
                width="spacing-40"
                height="font-size-m"
              />
              <span v-else>
                {{
                  globalCurrencyStore.formatMoney(
                    tripSummaryStore.tripGrossPrice +
                      tripSummaryStore.tripTotalAddonsPrice
                  )
                }}
              </span>
            </div>
            <div class="amount">
              <AppFlicker
                v-if="itineraryStore.isSearchingItineraries"
                width="spacing-64"
                height="font-size-h5"
                :margin="{ ySpacing: 'spacing-2' }"
              />
              <template v-else>
                <span v-if="tripSummaryStore.tripGrossPrice" class="discount">
                  {{
                    globalCurrencyStore.formatMoney(
                      tripSummaryStore.tripTotalPrice +
                        tripSummaryStore.tripTotalAddonsPrice
                    )
                  }}
                </span>

                <span v-else>
                  {{
                    globalCurrencyStore.formatMoney(
                      tripSummaryStore.tripTotalPrice +
                        tripSummaryStore.tripTotalAddonsPrice
                    )
                  }}
                </span>
              </template>
            </div>
          </div>
        </div>

        <div>
          <AppButton
            type="important"
            size="medium"
            :state="
              itineraryStore.isSearchingItineraries
                ? 'loadingDots'
                : props.buttonState
            "
            @click="emits('button-clicked')"
          >
            {{ props.buttonText }}
          </AppButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.price-summary-footer-with-save-trip-mobile {
  width: 100vw;
  .price-summary-footer-with-save-trip-mobile-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: column;
    gap: var(--spacing-12);
    padding: var(--spacing-12) var(--spacing-16) var(--spacing-16)
      var(--spacing-16);
    background-color: var(--color-white);
    opacity: 0.98;
    box-shadow: var(--shadow-elevation-30);

    .save-trip-button {
      width: 100%;
      gap: var(--spacing-4);
      background-color: var(--color-information-light);
      border-radius: var(--border-radius-8);
      padding: var(--spacing-8);

      .save-trip-button-content {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-4);

        .save-trip-button-icon {
          color: var(--color-neutral-70);
        }

        .save-trip-button-text {
          @include font-body-s-bold;
          text-decoration: underline;
          color: var(--color-neutral-70);
        }
      }
    }

    .price-and-button-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      .total-price {
        color: var(--color-neutral-70);
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--spacing-2);

        .text {
          @include font-body-s-light;
          &.gap {
            margin-top: var(--spacing-4);
          }
        }

        .price {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: var(--spacing-4);

          .amount {
            @include font-heading-5;
            .discount {
              color: var(--color-error-dark);
            }
          }

          .old-amount {
            @include font-label-m-bold;
            text-decoration: line-through;
          }
        }
      }
    }
  }
}
</style>
